import React, { useState, useEffect, useMemo } from "react";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
// import Box from "@mui/material/Box";
import ProfileTabsOfUser from "../components/ProfileTabsOfUser";
import { useParams } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { api } from "../utils/axios-instance";
import useAuth from "../stores/authStore";

const ProfilePicDummy = `${process.env.PUBLIC_URL}/assets/profilePicDummy.webp`;

const UserProfile = () => {
  const { userId } = useParams();

  const profileData = useAuth((state) => state.profileData);
  const currentUserId = profileData?.id;
  const currentRole = profileData?.role_priv;
  const canManage =
    currentRole === "org_admin" || currentRole === "super_admin";

  // Page state
  const [userProfileData, setUserProfileData] = useState(null);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedDeptIds, setSelectedDeptIds] = useState([]);

  // Whether the viewed user is an admin-role (determines dept UI behaviour)
  const viewedUserIsAdmin =
    selectedRole === "org_admin" || selectedRole === "super_admin";

  // Is org_admin viewing their own profile?
  const isSelf = userId && currentUserId && userId === currentUserId;

  // Dept dropdown: editable only for employees, not self, by a manager
  const deptEditable = canManage && !isSelf && !viewedUserIsAdmin;

  // Role dropdown: editable by manager, not on self
  const roleEditable = canManage && !isSelf;

  // id → name lookup
  const deptMap = useMemo(() => {
    const m = new Map();
    (departments || []).forEach((d) => m.set(d.id, d.name));
    return m;
  }, [departments]);

  // Fetch everything in parallel on mount
  useEffect(() => {
    let isMounted = true;

    const fetchAll = async () => {
      try {
        const [depRes, roleRes, userRes] = await Promise.all([
          api.get(`/dept/listDepts`),
          api.get(`/role/listRoles`),
          api.get(`/users/getUserInfo/${userId}/`),
        ]);

        if (!isMounted) return;

        const deps = depRes.data || [];
        const rolesData = roleRes.data || [];
        const info = userRes.data?.user_info || null;

        setDepartments(deps);
        setRoles(rolesData);
        setUserProfileData(info);

        const role = info?.role_priv || "";
        setSelectedRole(role);

        // Resolve initial dept selection
        const nameToId = Object.fromEntries(deps.map((d) => [d.name, d.id]));
        const isAdmin = role === "org_admin" || role === "super_admin";

        if (isAdmin) {
          // Admin/super_admin always have ALL depts
          setSelectedDeptIds(deps.map((d) => d.id));
        } else if (Array.isArray(info?.dept_ids) && info.dept_ids.length) {
          setSelectedDeptIds(info.dept_ids);
        } else if (info?.dept && nameToId[info.dept]) {
          setSelectedDeptIds([nameToId[info.dept]]);
        } else if (info?.dept && info.dept !== "All") {
          setSelectedDeptIds([info.dept]);
        } else {
          setSelectedDeptIds([]);
        }
      } catch (error) {
        console.error(error);
        toast.error("Something went wrong while loading profile.");
      }
    };

    if (userId) fetchAll();

    return () => {
      isMounted = false;
    };
  }, [userId]);

  // ALL role + dept mutations go through PATCH /users/elevate/{id}.
  // update-profile is only for self-profile display fields (name, pic, phone, address).
  const elevateUser = async (payload) => {
    await api.patch(`/users/elevate/${userProfileData.id}`, payload);
  };

  // Role change handler
  const handleRoleChange = async (event) => {
    const newRole = event.target.value;
    const prevRole = selectedRole;
    const prevDeptIds = [...selectedDeptIds];

    const isBecomingAdmin =
      newRole === "org_admin" || newRole === "super_admin";
    const isBecomingEmployee = newRole === "employee";

    // Build payload
    const patch = { role_priv: newRole };

    if (isBecomingEmployee) {
      // Must include dept_ids — backend hard-fails without them for employee role.
      // Priority: current selectedDeptIds → first available dept.
      const ids =
        selectedDeptIds.length > 0
          ? selectedDeptIds.filter(
              // exclude "ghost" all-dept ids that were set when user was admin
              // just keep whichever ids we already have
              Boolean
            )
          : departments.length
            ? [departments[0].id]
            : [];

      if (ids.length === 0) {
        toast.error("No departments available. Create one first.");
        return;
      }

      patch.dept_ids = ids;
      patch.dept_names = ids.map((id) => deptMap.get(id)).filter(Boolean);
    }
    // For admin/super_admin: backend overwrites depts with all — no dept_ids needed.

    // ── Optimistic UI update ──
    setSelectedRole(newRole);

    if (isBecomingAdmin) {
      // Show ALL depts selected immediately (backend will persist this too)
      setSelectedDeptIds(departments.map((d) => d.id));
    } else if (isBecomingEmployee) {
      setSelectedDeptIds(patch.dept_ids);
    }

    try {
      await elevateUser(patch);

      // After backend confirms, re-sync dept display for admin promotion
      // (backend may have a different list of all depts than our local state)
      if (isBecomingAdmin) {
        setSelectedDeptIds(departments.map((d) => d.id));
      }

      setUserProfileData((prev) => ({
        ...prev,
        role_priv: newRole,
        dept_ids: isBecomingAdmin
          ? departments.map((d) => d.id)
          : patch.dept_ids || prev?.dept_ids || [],
        dept_names: isBecomingAdmin
          ? departments.map((d) => d.name)
          : patch.dept_names || prev?.dept_names || [],
      }));

      toast.success("Role updated");
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.error || "Failed to update role.");
      // Roll back on failure
      setSelectedRole(prevRole);
      setSelectedDeptIds(prevDeptIds);
    }
  };

  // Department change handler
  const handleDeptChange = async (newDepartments) => {
    if (!canManage) return;

    if (viewedUserIsAdmin) {
      toast.error("Departments for admins are set to all automatically.");
      return;
    }

    if (newDepartments.length === 0) {
      toast.error("Employee must have at least one department.");
      return;
    }

    const dept_ids = newDepartments.map((d) => d.id);
    const dept_names = newDepartments.map((d) => d.name);
    const prevDeptIds = [...selectedDeptIds];

    // Optimistic update
    setSelectedDeptIds(dept_ids);

    try {
      await elevateUser({ dept_ids, dept_names });
      setUserProfileData((prev) => ({
        ...prev,
        dept_ids,
        dept_names,
      }));
      toast.success("Departments updated");
    } catch (error) {
      console.log(error);
      toast.error(
        error?.response?.data?.error || "Failed to update departments."
      );
      // Roll back
      setSelectedDeptIds(prevDeptIds);
    }
  };

  if (!userProfileData) return null;

  // Dept display text for the Autocomplete input
  const deptDisplayText = () => {
    if (viewedUserIsAdmin) return `All (${departments.length})`;
    const count = selectedDeptIds.length;
    if (count === 0) return "";
    if (count === 1) {
      return deptMap.get(selectedDeptIds[0]) || "";
    }
    return `${deptMap.get(selectedDeptIds[0]) || ""} +${count - 1}`;
  };

  return (
    <div className="p-4">
      <Toaster position="bottom-left" reverseOrder={false} />

      <div className="p-4 border shadow-lg bg-[#F1F1FF] border-gray-300 w-full rounded-xl ">
        <div className="flex flex-row items-center space-x-4">
          <img
            src={
              userProfileData && userProfileData.profilePictureUrl
                ? userProfileData.profilePictureUrl
                : ProfilePicDummy
            }
            alt="ProfilePic"
            className="rounded-full w-24 h-24 object-cover"
          />
          <div className="flex flex-col leading-9">
            <h3 className="text-lg font-semibold">
              {userProfileData.username
                ? `@${userProfileData.username}`
                : "UserName"}
            </h3>
            <h5 className="text-md font-semibold text-gray-700">
              {selectedRole || "Position"}
            </h5>
            <p className="text-sm text-gray-500">
              {`${userProfileData.address?.city || "No city"}, ${
                userProfileData.address?.state || "No state"
              }, ${userProfileData.address?.country || "No country"}`}
            </p>
          </div>
        </div>

        {/* Personal Information */}
        <h4 className="my-2 text-lg font-semibold ">Personal Information</h4>
        <div className="grid grid-cols-4 gap-4">
          <span>
            <h5 className="p-2 font-semibold">First Name</h5>
            <input
              value={userProfileData.first_name || ""}
              className={`text-md  placeholder-gray-300 bg-[#464F6029] shadow p-2 rounded-md w-full`}
              disabled
            />
          </span>

          <span>
            <h5 className="p-2 font-semibold">Last Name</h5>
            <input
              value={userProfileData.last_name || ""}
              className={`text-md  placeholder-gray-500 bg-[#464F6029] shadow p-2 rounded-md w-full`}
              disabled
            />
          </span>

          <span>
            <h5 className="p-2 font-semibold">Email Address</h5>
            <input
              value={userProfileData.email || ""}
              className={`text-md  placeholder-gray-500 bg-[#464F6029] shadow p-2 rounded-md w-full`}
              disabled
            />
          </span>

          <span>
            <h5 className="p-2 font-semibold">Phone Number</h5>
            <input
              value={userProfileData.contact.phone || ""}
              className={`text-md lining-nums placeholder-gray-500 bg-[#464F6029] shadow p-2 rounded-md w-full`}
              placeholder="Phone Number"
              disabled
            />
          </span>
        </div>

        {/* Work Information */}
        <h4 className="my-2 text-lg font-semibold ">Work Information</h4>
        <div className="grid grid-cols-4 gap-4">
          {/* Department(s) */}
          <span>
            <h5 className="p-2 font-semibold">Department(s)</h5>
            <Autocomplete
              multiple
              options={departments}
              getOptionLabel={(option) => option.name}
              // Drive selected state from dept_ids
              value={departments.filter((d) => selectedDeptIds.includes(d.id))}
              disableCloseOnSelect
              onChange={(event, newValue) => handleDeptChange(newValue)}
              disabled={!deptEditable}
              renderTags={() => null} // tags hidden; we render custom text in input
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox checked={selected} sx={{ mr: 1 }} />
                  {/* dept colour dot, same as table */}
                  <span
                    className="w-3 h-3 rounded-full inline-block mr-2 flex-shrink-0"
                    style={{ backgroundColor: option?.metadata?.bg }}
                  />
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  // Show custom summary text, not individual chips
                  value={deptDisplayText()}
                  size="small"
                  inputProps={{
                    ...params.inputProps,
                    // Don't let MUI override our display text
                    readOnly: true,
                    value: deptDisplayText(),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      height: 40,
                      fontSize: 13,
                      backgroundColor: deptEditable ? "#fff" : "#F9FAFB",
                    },
                    "& input": {
                      cursor: deptEditable ? "pointer" : "default",
                    },
                    "& input::placeholder": { display: "none" },
                  }}
                />
              )}
              sx={{
                width: "100%",
                "& .MuiAutocomplete-tag": { display: "none" },
              }}
            />
          </span>

          {/* Role */}
          <span>
            <h5 className="p-2 font-semibold">Role</h5>
            <Select
              value={selectedRole || ""}
              onChange={handleRoleChange}
              size="small"
              disabled={!roleEditable}
              // className="w-full"
              fullWidth
              sx={{
                borderRadius: "6px",
                backgroundColor: roleEditable ? "#fff" : "#464F6029",
                "& .MuiSelect-select": { py: 1 },
              }}
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.role}>
                  {role.role}
                </MenuItem>
              ))}
            </Select>
          </span>
        </div>
      </div>
      <div className="pt-4">
        <ProfileTabsOfUser />
      </div>
    </div>
  );
};

export default UserProfile;
